import { db } from '@/data/firebase/config';
import { useRBACContext } from '@/domain/context/rbac';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import { fetchAudiencesEnabled } from '@/domain/use-cases/audiences/audience-settings';
import {
  fetchNotificationSettings,
  findUserByDocument,
} from '@/domain/use-cases/notifications/notification-settings';
import AlertMessage from '@/presentation/components/alert-message';
import Button from '@/presentation/components/button';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const ACTION_BUTTON_CLASS = 'shrink-0 whitespace-nowrap min-w-[11rem]';

const SettingsPage = () => {
  const { loading: rbacLoading, hasPermission } = useRBACContext();
  const canAccessSettings = hasPermission('settings', 'read');
  const { logUpdate } = useAdminLogger();

  const [showRegister, setShowRegister] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [audiencesEnabled, setAudiencesEnabled] = useState(false);
  const [isAudienceLoading, setIsAudienceLoading] = useState(true);
  const [isAudienceConfirmOpen, setIsAudienceConfirmOpen] = useState(false);
  const [isAudienceSaving, setIsAudienceSaving] = useState(false);

  const [notificationTestMode, setNotificationTestMode] = useState(false);
  const [devDocument, setDevDocument] = useState('');
  const [devDocumentInput, setDevDocumentInput] = useState('');
  const [isNotificationLoading, setIsNotificationLoading] = useState(true);
  const [isNotificationConfirmOpen, setIsNotificationConfirmOpen] =
    useState(false);
  const [isNotificationSaving, setIsNotificationSaving] = useState(false);
  const [isDevDocumentSaving, setIsDevDocumentSaving] = useState(false);

  const loadShowRegister = async () => {
    setIsLoading(true);
    try {
      const docSnap = await getDoc(doc(db, DOCUMENTS_DB.CONFIG, 'auth'));
      setShowRegister(
        docSnap.exists() ? docSnap.data().show_register === true : false,
      );
    } catch (error) {
      console.error('Error al cargar la configuración:', error);
      toast.error('No se pudo cargar la configuración', {
        theme: 'colored',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAudiencesEnabled = async () => {
    setIsAudienceLoading(true);
    const enabled = await fetchAudiencesEnabled();
    setAudiencesEnabled(enabled);
    setIsAudienceLoading(false);
  };

  const loadNotificationSettings = async () => {
    setIsNotificationLoading(true);
    const settings = await fetchNotificationSettings();
    setNotificationTestMode(settings.testMode);
    setDevDocument(settings.devDocument);
    setDevDocumentInput(settings.devDocument);
    setIsNotificationLoading(false);
  };

  useEffect(() => {
    loadShowRegister();
    loadAudiencesEnabled();
    loadNotificationSettings();
  }, []);

  const handleToggleConfirmed = async () => {
    setIsConfirmOpen(false);
    setIsSaving(true);
    const nextValue = !showRegister;
    try {
      await updateDoc(doc(db, DOCUMENTS_DB.CONFIG, 'auth'), {
        show_register: nextValue,
      });
      setShowRegister(nextValue);
      toast.success(
        nextValue
          ? 'Registro de usuarios activado en la app móvil'
          : 'Registro de usuarios desactivado en la app móvil',
        { theme: 'colored' },
      );
    } catch (error) {
      console.error('Error al actualizar show_register:', error);
      toast.error('No se pudo actualizar la configuración', {
        theme: 'colored',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAudienceToggleConfirmed = async () => {
    setIsAudienceConfirmOpen(false);
    setIsAudienceSaving(true);
    const nextValue = !audiencesEnabled;
    try {
      await setDoc(
        doc(db, DOCUMENTS_DB.CONFIG, 'audienceSettings'),
        { enabled: nextValue },
        { merge: true },
      );
      await logUpdate(
        DOCUMENTS_DB.CONFIG,
        'audienceSettings',
        'Filtrado por audiencias',
        { enabled: nextValue },
      );
      setAudiencesEnabled(nextValue);
      toast.success(
        nextValue
          ? 'Filtrado por audiencias activado'
          : 'Filtrado por audiencias desactivado',
        { theme: 'colored' },
      );
    } catch (error) {
      console.error('Error al actualizar audienceSettings:', error);
      toast.error('No se pudo actualizar la configuración de audiencias', {
        theme: 'colored',
      });
    } finally {
      setIsAudienceSaving(false);
    }
  };

  const handleNotificationToggleConfirmed = async () => {
    setIsNotificationConfirmOpen(false);
    setIsNotificationSaving(true);
    const nextValue = !notificationTestMode;
    try {
      await setDoc(
        doc(db, DOCUMENTS_DB.CONFIG, 'notificationSettings'),
        { testMode: nextValue },
        { merge: true },
      );
      await logUpdate(
        DOCUMENTS_DB.CONFIG,
        'notificationSettings',
        'Modo de prueba de notificaciones',
        { testMode: nextValue },
      );
      setNotificationTestMode(nextValue);
      toast.success(
        nextValue ? 'Modo de prueba activado' : 'Modo de prueba desactivado',
        { theme: 'colored' },
      );
    } catch (error) {
      console.error('Error al actualizar notificationSettings:', error);
      toast.error('No se pudo actualizar el modo de prueba de notificaciones', {
        theme: 'colored',
      });
    } finally {
      setIsNotificationSaving(false);
    }
  };

  const handleSaveDevDocument = async () => {
    setIsDevDocumentSaving(true);
    const value = devDocumentInput.trim();
    try {
      const lookup = await findUserByDocument(value);
      if (!lookup.found) {
        toast.error(
          'No se encontró ningún usuario con ese número de documento',
          { theme: 'colored' },
        );
        return;
      }
      await setDoc(
        doc(db, DOCUMENTS_DB.CONFIG, 'notificationSettings'),
        { devDocument: value },
        { merge: true },
      );
      await logUpdate(
        DOCUMENTS_DB.CONFIG,
        'notificationSettings',
        'Documento de prueba de notificaciones',
        { devDocument: value },
      );
      setDevDocument(value);
      if (lookup.hasPushToken) {
        toast.success(
          `Las notificaciones de prueba llegarán a: ${lookup.name}`,
          { theme: 'colored' },
        );
      } else {
        toast.warning(
          `Se guardó, pero ${lookup.name} todavía no tiene un token de notificaciones registrado — las pruebas no le llegarán hasta que abra la app`,
          { theme: 'colored' },
        );
      }
    } catch (error) {
      console.error('Error al actualizar devDocument:', error);
      toast.error('No se pudo guardar el documento de prueba', {
        theme: 'colored',
      });
    } finally {
      setIsDevDocumentSaving(false);
    }
  };

  if (rbacLoading || isLoading || isAudienceLoading || isNotificationLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <span>Cargando...</span>
      </div>
    );
  }

  if (!canAccessSettings) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-gray-500">No tienes acceso a esta sección.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="text-2xl font-bold">Configuración</h1>
      <div className="border rounded-lg p-4 flex items-center justify-between gap-6 max-w-2xl">
        <div className="flex flex-col gap-1">
          <span className="font-medium">
            Registro de nuevos usuarios (app móvil)
          </span>
          <span className="text-sm text-gray-500">
            Actívalo antes de enviar la app a revisión en las tiendas;
            desactívalo tras la publicación.
          </span>
          <span className="text-sm">
            Estado actual:{' '}
            <strong
              data-testid="show-register-status"
              className={showRegister ? 'text-green-600' : 'text-red-600'}
            >
              {showRegister ? 'Activado' : 'Desactivado'}
            </strong>
          </span>
        </div>
        <Button
          label={showRegister ? 'Desactivar registro' : 'Activar registro'}
          variant={showRegister ? 'danger' : 'primary'}
          onClick={() => setIsConfirmOpen(true)}
          isLoading={isSaving}
          disabled={isSaving}
          className={ACTION_BUTTON_CLASS}
        />
      </div>
      <AlertMessage
        isOpen={isConfirmOpen}
        title={showRegister ? 'Desactivar registro' : 'Activar registro'}
        message={
          showRegister
            ? '¿Seguro que quieres desactivar el registro de nuevos usuarios en la app móvil?'
            : '¿Seguro que quieres activar el registro de nuevos usuarios en la app móvil?'
        }
        onConfirm={handleToggleConfirmed}
        onCancel={() => setIsConfirmOpen(false)}
      />

      <div className="border rounded-lg p-4 flex items-center justify-between gap-6 max-w-2xl">
        <div className="flex flex-col gap-1">
          <span className="font-medium">Filtrado por audiencias</span>
          <span className="text-sm text-gray-500">
            Restringe publicaciones a audiencias específicas en toda la
            plataforma. Actívalo solo cuando la versión de la app móvil que sabe
            filtrar por audiencia tenga adopción suficiente.
          </span>
          <span className="text-sm">
            Estado actual:{' '}
            <strong
              data-testid="audiences-status"
              className={audiencesEnabled ? 'text-green-600' : 'text-red-600'}
            >
              {audiencesEnabled ? 'Activado' : 'Desactivado'}
            </strong>
          </span>
        </div>
        <Button
          label={
            audiencesEnabled ? 'Desactivar audiencias' : 'Activar audiencias'
          }
          variant={audiencesEnabled ? 'danger' : 'primary'}
          onClick={() => setIsAudienceConfirmOpen(true)}
          isLoading={isAudienceSaving}
          disabled={isAudienceSaving}
          className={ACTION_BUTTON_CLASS}
        />
      </div>
      <AlertMessage
        isOpen={isAudienceConfirmOpen}
        title={
          audiencesEnabled
            ? 'Desactivar filtrado por audiencias'
            : 'Activar filtrado por audiencias'
        }
        message={
          audiencesEnabled
            ? '¿Seguro que quieres desactivar el filtrado por audiencias? Todas las publicaciones volverán a ser visibles para todos los usuarios.'
            : '¿Seguro que quieres activar el filtrado por audiencias? Asegurate de que suficientes usuarios ya tengan instalada la versión de la app móvil que sabe filtrar por audiencia — quienes sigan con una versión anterior no verán las publicaciones restringidas hasta que actualicen, y de que exista adopción suficiente antes de continuar.'
        }
        onConfirm={handleAudienceToggleConfirmed}
        onCancel={() => setIsAudienceConfirmOpen(false)}
      />

      <div className="border rounded-lg p-4 flex flex-col gap-3 max-w-2xl">
        <div className="flex items-center justify-between gap-6">
          <div className="flex flex-col gap-1">
            <span className="font-medium">
              Modo de prueba de notificaciones push
            </span>
            <span className="text-sm text-gray-500">
              Mientras esté activo, todas las notificaciones push van solo al
              documento configurado abajo, no a los usuarios reales.
            </span>
            <span className="text-sm">
              Estado actual:{' '}
              <strong
                data-testid="notification-testmode-status"
                className={
                  notificationTestMode ? 'text-green-600' : 'text-red-600'
                }
              >
                {notificationTestMode ? 'Activado' : 'Desactivado'}
              </strong>
            </span>
          </div>
          <Button
            label={
              notificationTestMode
                ? 'Desactivar modo prueba'
                : 'Activar modo prueba'
            }
            variant={notificationTestMode ? 'danger' : 'primary'}
            onClick={() => setIsNotificationConfirmOpen(true)}
            isLoading={isNotificationSaving}
            disabled={isNotificationSaving}
            className={ACTION_BUTTON_CLASS}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
          <label
            htmlFor="dev-document-input"
            className="text-sm text-gray-500 shrink-0"
          >
            Documento que recibe las pruebas:
          </label>
          <input
            id="dev-document-input"
            type="text"
            data-testid="dev-document-input"
            value={devDocumentInput}
            onChange={(e) => setDevDocumentInput(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary flex-1 min-w-[8rem]"
          />
          <Button
            label="Guardar documento"
            variant="secondary"
            onClick={handleSaveDevDocument}
            isLoading={isDevDocumentSaving}
            disabled={isDevDocumentSaving || devDocumentInput.trim() === ''}
            className={ACTION_BUTTON_CLASS}
          />
        </div>
      </div>
      <AlertMessage
        isOpen={isNotificationConfirmOpen}
        title={
          notificationTestMode
            ? 'Desactivar modo de prueba'
            : 'Activar modo de prueba'
        }
        message={
          notificationTestMode
            ? '¿Seguro que quieres desactivar el modo de prueba? Las notificaciones push volverán a enviarse a todos los usuarios.'
            : `¿Seguro que quieres activar el modo de prueba? Mientras esté activo, ningún usuario real recibirá notificaciones push — todas irán solo al documento configurado (${devDocument || 'sin configurar'}).`
        }
        onConfirm={handleNotificationToggleConfirmed}
        onCancel={() => setIsNotificationConfirmOpen(false)}
      />
    </div>
  );
};

export default SettingsPage;
